import { fetchCurrentUser, generateTelegramLinkToken } from '../api';
import { Env } from '@/shared/config/environment';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 300;

const state = {
  link: null,
  qrCode: null,
  expiresAt: null,
  isLinked: false,
  telegramUsername: null,
  isLoadingLink: false,
  error: null,
  isPolling: false,
  pollAttempts: 0,
  pollTimer: null,
};

const mutations = {
  SET_LINK(state, link) {
    state.link = link;
  },
  SET_QR_CODE(state, qrCode) {
    state.qrCode = qrCode;
  },
  SET_EXPIRES_AT(state, expiresAt) {
    state.expiresAt = expiresAt;
  },
  SET_IS_LINKED(state, isLinked) {
    state.isLinked = isLinked;
  },
  SET_TELEGRAM_USERNAME(state, telegramUsername) {
    state.telegramUsername = telegramUsername;
  },
  SET_LOADING_LINK(state, isLoadingLink) {
    state.isLoadingLink = isLoadingLink;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_POLLING(state, isPolling) {
    state.isPolling = isPolling;
  },
  INCREMENT_POLL_ATTEMPTS(state) {
    state.pollAttempts++;
  },
  RESET_POLL_ATTEMPTS(state) {
    state.pollAttempts = 0;
  },
  SET_POLL_TIMER(state, timer) {
    state.pollTimer = timer;
  },
};

const actions = {
  async initLink({ commit, dispatch }) {
    commit('SET_LOADING_LINK', true);
    commit('SET_ERROR', null);
    commit('SET_IS_LINKED', false);

    try {
      const currentUser = await fetchCurrentUser();

      if (currentUser?.telegramLinked) {
        commit('SET_IS_LINKED', true);
        commit('SET_TELEGRAM_USERNAME', currentUser?.telegramUsername);
        return;
      }

      const response = await generateTelegramLinkToken();
      commit('SET_LINK', response?.link);
      commit('SET_QR_CODE', response?.qrCode);
      commit('SET_EXPIRES_AT', response?.expiresAt);

      dispatch('startPolling');
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося згенерувати посилання для привʼязки';

      commit('SET_ERROR', errorMessage);
      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося згенерувати посилання',
          message: errorMessage,
        }, { root: true });
      }
    } finally {
      commit('SET_LOADING_LINK', false);
    }
  },
  startPolling({ commit, dispatch, state }) {
    if (state.pollTimer) {
      clearInterval(state.pollTimer);
    }

    commit('RESET_POLL_ATTEMPTS');
    commit('SET_POLLING', true);

    const timer = setInterval(async () => {
      commit('INCREMENT_POLL_ATTEMPTS');

      if (state.pollAttempts > MAX_POLL_ATTEMPTS || (state.expiresAt && Date.now() / 1000 > state.expiresAt)) {
        dispatch('stopPolling');
        commit('SET_ERROR', 'Час дії посилання вичерпано. Згенеруйте нове.');
        return;
      }

      try {
        const response = await fetchCurrentUser();

        if (response?.telegramLinked) {
          commit('SET_IS_LINKED', true);
          commit('SET_TELEGRAM_USERNAME', response?.telegramUsername);
          dispatch('stopPolling');
        }
      } catch {
        dispatch('stopPolling');
        commit('SET_ERROR', 'Не вдалося перевірити статус привʼязки.');
      }
    }, POLL_INTERVAL_MS);

    commit('SET_POLL_TIMER', timer);
  },
  stopPolling({ commit, state }) {
    if (state.pollTimer) {
      clearInterval(state.pollTimer);
    }
    commit('SET_POLL_TIMER', null);
    commit('SET_POLLING', false);
    commit('RESET_POLL_ATTEMPTS');
  },
};

const getters = {
  link: (state) => state.link,
  qrCode: (state) => state.qrCode,
  isLinked: (state) => state.isLinked,
  telegramUsername: (state) => state.telegramUsername,
  isLoadingLink: (state) => state.isLoadingLink,
  error: (state) => state.error,
  isPolling: (state) => state.isPolling,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
